// @flow
/* eslint react/jsx-no-bind:0 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as Cookie from 'app/packages/cookie-utils';
import AlternativesSeparator from 'app/common/react/components/growth/unauth/signup/AlternativesSeparator/AlternativesSeparator';
import CONST from 'app/common/lib/Const';
import FacebookConnectButton from 'app/common/react/components/growth/unauth/signup/FacebookConnectButton/FacebookConnectButton';
import GoogleConnectButton from 'app/common/react/components/growth/unauth/signup/GoogleConnectButton/GoogleConnectButton';
import i18n from 'app/common/lib/i18n';
import isMistypedGmail from 'app/packages/email-validation-utils';
import login from 'app/common/react/components/growth/unauth/signup/UserLogin';
import PhoneAuth from 'app/common/react/components/growth/unauth/signup/PhoneAuth/PhoneAuth';
import PropTypes from 'prop-types';
import RegisterForm from 'app/common/react/components/growth/unauth/signup/RegisterForm/RegisterForm';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import runValidators from 'app/common/react/components/growth/unauth/lib/ValidationUtils';
import TermsOfService from 'app/common/react/components/growth/unauth/signup/TermsOfService/TermsOfService';
import touchableErrorMessage from 'app/common/react/components/growth/unauth/signup/AccountRecovery/TouchableErrorMessage';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { Router } from 'app/common/lib/ReactRouter/types';
import type { SignupOptions } from 'app/packages/pinterest-flow-types/registration';
import type { State } from 'app/common/redux/types';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import UserRegistration from 'app/common/react/components/growth/unauth/signup/UserRegistration';
import { BoldFontFamily } from 'app/common/react/components/common/FontFamily';
import { Button } from 'gestalt';
import { compose } from 'redux';
import { showError } from 'app/common/react/components/growth/unauth/lib/ErrorUtils';
import { withRouter } from 'react-router';
import AccountRecovery, {
  ACCT_RECOVERY_MODAL_MAPPING,
} from 'app/common/react/components/growth/unauth/signup/AccountRecovery/AccountRecovery';
import loginUtil, { type ModalTypes } from 'app/www-unauth/lib/loginUtil';
import type {
  FacebookUserData,
  LoginCreds,
} from 'app/packages/pinterest-flow-types/authentication';
import withContextLog, { type ContextLog } from 'app/common/lib/react/deprecated/withContextLog'; // eslint-disable-line pinterest/no-deprecated-imports
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  Age,
  ContainsOnlyAscii,
  Email,
  Length,
  NewPassword,
  Required,
} from 'app/common/lib/react/validator';
import {
  registrationRequiresAgeEN,
  registrationRequiresAgeGDPR,
} from 'app/packages/pinterest-authentication/registration';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { MultiTreatmentExp, SingleTreatmentExp } from 'app/common/lib/experiment';

const styles = {
  emailButtonStyles: {
    height: '40px',
    width: '100%',
    borderRadius: '2px',
  },
  emailButtonTextStyles: {
    fontFamily: BoldFontFamily,
    textAlign: 'center',
    fontSize: '15px',
    WebkitFontSmoothing: 'auto', // make the font style in consitent with facebook and google buttons
  },
  spaceBetweenTextAndSocial: {
    height: '11px',
  },
  signupContainer: {
    margin: '0 auto',
    width: '268px',
  },
  socialButtonContainer: {
    marginTop: '10px',
  },
  socialButtonSeparator: {
    height: '10px',
  },
};

const pageName = 'emailpwdstep';

type OwnProps = {|
  additionalChildren?: any,
  age?: string,
  ageValidationError?: string,
  container?: string,
  dark?: boolean,
  disablePostSignupNextUrl?: boolean,
  email: string,
  emailValidationError?: string,
  loadingIndicatorCallback: (on: boolean) => void,
  passwordValidationError?: string,
  setWelcomeBackMessageForExistingEmail?: (shouldSet: boolean) => void,
  inputStyleOverrides?: Object,
  name?: string,
  newEmail?: string,
  nextUrlParam?: string,
  onAgeChange: ({ event: SyntheticInputEvent<HTMLInputElement>, value: string }) => void,
  onEmailChange: (e: SyntheticEvent<>) => void,
  onFBEmailDeniedPerm?: (
    network: string,
    userData: FacebookUserData,
    credentialsAndLoggingData: SignupOptions,
  ) => void,
  onSocialConnectVerified?: (
    network: string,
    userData: FacebookUserData,
    credentialsAndLoggingData: SignupOptions,
  ) => void,
  onLoginWithMfa: (creds: LoginCreds, Object) => void,
  onPasswordChange: (e: SyntheticEvent<>) => void,
  password: string,
  setNameFromEmail?: () => void,
  showLoginWelcomeLoadingFn?: (boolean) => void,
  showSignupWelcomeLoadingFn?: (boolean) => void,
  signupSource?: string,
  signupSeparatorContinuous?: boolean,
  sm?: string,
  suppressFocus?: boolean,
  updateEmail?: (email: string) => void,
  clearPassword?: () => void,
  onShowRegisterForm?: () => void,
  showResponseMessageModal?: (isSignup: boolean) => void,
|};

type ReduxProps = {|
  viewer: Viewer,
|};

type EmailPasswordStepProps = {|
  ...OwnProps,
  ...ReduxProps,
  requestContext: RequestContextType,
  contextLog: ContextLog,
  router: Router,
  seoUnauthExperiments: UnauthSeoExperiments,
|};

type EmailPasswordStepState = {|
  ageValidationError: ?string,
  accountRecoveryModalType: ?ModalTypes,
  emailValidationError: ?string,
  facebookValidationError: ?string,
  googleValidationError: ?string,
  passwordValidationError: ?string | React$Element<any>,
  showRegisterForm: ?boolean,
  emailFieldHasError: ?boolean,
|};

type Context = {|
  country: string,
  locale: string,
  userAgent: {
    isMobile: boolean,
  },
  hybridTier?: string,
  pageType?: string,
|};

export class EmailPasswordStep extends Component<EmailPasswordStepProps, EmailPasswordStepState> {
  constructor(props: EmailPasswordStepProps, context: Context) {
    super(props, context);
    this.state = {
      ageValidationError: props.ageValidationError || null,
      accountRecoveryModalType: null,
      emailFieldHasError: false,
      emailValidationError: props.emailValidationError || null,
      facebookValidationError: null,
      googleValidationError: null,
      passwordValidationError: props.passwordValidationError || null,
      showRegisterForm: this.props.container === 'home_page',
    };

    this.emailValidators = [
      Required({ message: i18n._("You missed a spot! Don't forget to add your email.") }),
      Email({ message: i18n._("Hmm... that doesn't look like an email address.") }),
    ];

    const shortPasswordValidatorMessage = i18n._(
      'Your password is too short! You need 6+ characters.',
    );
    const noZanyCharactersValidatorMessage = i18n._('Oh @*&#! No zany characters allowed.');
    const requireAgeValidatorMessage = i18n._(
      'Help us protect you by providing your age',
      'Error message shown when user did not fill the age field in the sign up model',
    );

    this.passwordValidators = [
      Required({ message: shortPasswordValidatorMessage }),
      NewPassword({
        shortPassStr: shortPasswordValidatorMessage,
        samePasswordStr: i18n._('Please make your username and password different.'),
        blackPassStr: i18n._('Please make a stronger password.'),
      }),
    ];

    this.asciiValidators = [ContainsOnlyAscii({ message: noZanyCharactersValidatorMessage })];

    this.loginPasswordValidators = [
      Required({ message: shortPasswordValidatorMessage }),
      ContainsOnlyAscii({ message: noZanyCharactersValidatorMessage }),
      Length({
        messages: { min: shortPasswordValidatorMessage },
        min: 6,
      }),
    ];

    if (this.shouldRequireAge()) {
      this.ageValidators = [
        Required({ message: requireAgeValidatorMessage }),
        Age({ message: i18n._('Please enter a valid number.') }),
      ];
    } else {
      this.ageValidators = [Age({ message: i18n._('Please enter a valid number.') })];
    }
  }

  // TODO (iris): remove this after this is translated for copytune
  stringsToTranslate() {
    i18n._(
      'Oh @*&#! No zany characters allowed.',
      'Error message shown to users who entered a password with non ascii characters.',
    );
  }

  componentDidMount() {
    PhoneAuth.setupPhoneAuth(this.context.locale);
  }

  UNSAFE_componentWillReceiveProps(nextProps: EmailPasswordStepProps) {
    const { ageValidationError, emailValidationError, passwordValidationError } = nextProps;
    let nextState = {};

    if (emailValidationError !== this.props.emailValidationError) {
      nextState = { emailValidationError };
    } else if (passwordValidationError !== this.props.passwordValidationError) {
      nextState = { passwordValidationError };
    } else if (ageValidationError !== this.props.ageValidationError) {
      nextState = { ageValidationError };
    }

    this.setState(nextState);
  }

  onLoginWithSuspended = () => {
    const { loadingIndicatorCallback } = this.props;

    loadingIndicatorCallback(false);
    this.setState({
      accountRecoveryModalType: ACCT_RECOVERY_MODAL_MAPPING[CONST.POLICY_VIOLATION_USER],
    });
  };

  onLoginWithVoluntarilyDeactivated = (error: Object) => {
    this.setState({
      googleValidationError: error.message,
    });
  };

  setLoadingState = (state: boolean) => {
    const { loadingIndicatorCallback, showSignupWelcomeLoadingFn } = this.props;
    if (showSignupWelcomeLoadingFn && state === false) {
      // Welcome Screen is for experiment users.
      // Only allow this function to turn off the Welcome Screen.
      // We specifically turn on the Welcome Screen in other places after checking the experiment.
      showSignupWelcomeLoadingFn(false);
    }
    return loadingIndicatorCallback(state);
  };

  getButtonText() {
    let intlContinueText = i18n._('Continue', 'Continue button on web');
    if (this.context.userAgent.isMobile) {
      intlContinueText = i18n._('Continue', 'Continue button on mobile web');
    }
    return intlContinueText;
  }

  asciiValidators: Array<*>;
  emailValidators: Array<*>;
  passwordValidators: Array<*>;
  loginPasswordValidators: Array<*>;
  ageValidators: Array<*>;
  googleButton: ?HTMLDivElement;

  context: Context;

  handleBlur(inputType: string) {
    if (inputType === 'id') {
      this.setState({ emailValidationError: null });
    } else if (inputType === 'age') {
      this.setState({ ageValidationError: null });
    }
    this.setState({ facebookValidationError: null, googleValidationError: null });
  }

  handleButtonClick = (e: SyntheticEvent<>) => {
    if (Cookie.get(PCONST.COOKIE_REGISTER_RESTRICT) === '1') {
      this.showUnderAgeError();
      return;
    }

    const phoneError = PhoneAuth.runPhoneValidators(
      this.props.email,
      this.context.locale,
      this._loginFacebookAccountKit.bind(this),
    );
    this.setState({ facebookValidationError: null, googleValidationError: null });
    if (phoneError) {
      this.handleEmailSignUp(e);
    }
  };

  handleEmailChange = (e: SyntheticEvent<>) => {
    if (this.props.onEmailChange) {
      this.props.onEmailChange(e);
    }
  };

  handleTypoSuggestionClick = () => {
    this.setState({
      emailValidationError: '',
    });
  };

  handlePasswordChange = (e: SyntheticEvent<>) => {
    if (this.state.passwordValidationError) {
      this.setState({ passwordValidationError: null });
    }
    if (this.props.onPasswordChange) {
      this.props.onPasswordChange(e);
    }
  };

  handleEmailSignUp = (e: SyntheticEvent<>) => {
    trackRegisterAction('unauth_email_validation_attempt');
    let error = runValidators(this.props.email, this.emailValidators);
    if (error) {
      this.setState({ emailValidationError: error });
      trackRegisterAction(`unauth.login.error.CLIENT_ERROR.malformemail.${pageName}`);
    } else {
      error = runValidators(this.props.password, this.loginPasswordValidators);
      // Note: we are splitting ascii check into its own validator to gauge specific impact
      // TODO(rmist): add ascii validator back to loginPasswordValidators if we no longer need to log
      const asciiError = runValidators(this.props.password, this.asciiValidators);
      if (error) {
        this.setState({ passwordValidationError: error });
        trackRegisterAction(`unauth.login.error.CLIENT_ERROR.malformpwd.${pageName}`);
      } else if (asciiError) {
        // Still log as error
        this.setState({ passwordValidationError: asciiError });
        trackRegisterAction(`unauth.login.error.CLIENT_ERROR.malformpwd.${pageName}`);
        // Log specific non-ascii errors
        trackRegisterAction(`unauth.login.error.CLIENT_ERROR.nonasciipwd.${pageName}`);
      } else {
        this.setState({ passwordValidationError: null });
        this.validateEmailAvailable(e);
      }
      this.setState({ emailValidationError: null });
      // Only infer full name from email for desktop users
      if (this.props.setNameFromEmail && !this.context.userAgent.isMobile) {
        // this sets the parent's state{name} which trickles down as a prop{name} later
        // in this specific flow, the name gets set after the 1st login/existence call, but before the 2nd call to sign-up
        this.props.setNameFromEmail();
      }
    }
  };

  _loginFacebookAccountKit = (response: Object) => {
    if (PhoneAuth.validateResponse(response)) {
      // successful Account Kit flow, perform Pinterest login
      const creds = {
        phone_number: this.props.email,
        fb_auth_code: response.code,
      };
      trackRegisterAction('unauth_phone_only_login_attempt');
      this.login(creds);
    } else {
      // user closed Account Kit popup
      this.setState({
        emailValidationError: i18n._('Enter your email or phone number to try again.'),
      });
      this.setLoadingState(false);
    }
  };

  handleLoginFailure(error: Object, creds: Object, fromExperiment: boolean = false) {
    const { clearPassword, email, onLoginWithMfa } = this.props;
    this.contextLogSignupLoginEvent(PCONST.thrift.EventType.LOGIN_FAILURE);
    const api_error_code =
      error.http_status === CONST.RESPONSE_CODE_TOO_MANY_REQUESTS
        ? CONST.RESPONSE_CODE_TOO_MANY_REQUESTS
        : error.api_error_code;

    if (api_error_code === CONST.LOGIN_MFA_REQUIRED) {
      loginUtil.handleLoginMfa(creds, error.data, onLoginWithMfa);
      return;
    }

    const errorState = loginUtil.getLoginErrorState(
      error,
      'emailPasswordStep',
      email,
      touchableErrorMessage,
      this.updateAccountRecoveryModal,
      this.props.seoUnauthExperiments,
    );

    if (errorState) {
      this.setState({
        accountRecoveryModalType: ACCT_RECOVERY_MODAL_MAPPING[api_error_code],
        ...errorState,
      });
      if (clearPassword && errorState.passwordValidationError) {
        clearPassword();
      }
      this.setLoadingState(false);
    } else {
      // wait for translation with new context
      i18n._(
        'Sorry! Something went wrong on our end.',
        "Error message shown when user tried to login but error occured, and we don't have any other information about the error. May or may not be recoverable by the user.",
      );
      showError(i18n._('Sorry! Something went wrong on our end.'));
      this.setLoadingState(false);
    }
  }

  handleLoginSuccess(creds: Object) {
    this.contextLogSignupLoginEvent(PCONST.thrift.EventType.LOGIN_SUCCESS);
    loginUtil.storeLoginCredentialsToBrowser(creds);
    const { showResponseMessageModal } = this.props;
    if (showResponseMessageModal) {
      showResponseMessageModal(false);
    } else {
      window.location.reload();
    }
  }

  login(altCreds: Object) {
    this.setLoadingState(true);
    const creds = altCreds || {
      username_or_email: this.props.email,
      password: this.props.password,
    };
    const loggingData = {
      container: this.props.container,
      hybridTier: this.context.hybridTier,
      page: this.context.pageType,
    };
    login(creds, loggingData, this.props.seoUnauthExperiments).then(
      () => {
        this.handleLoginSuccess(creds);
      },
      (error) => {
        if (error.api_error_code === CONST.INVALID_PASSWORD_ERROR_CODE) {
          this.setState({ passwordValidationError: error.message });
          this.setLoadingState(false);
        } else if (error.api_error_code === CONST.INVALID_PASSWORD_FB_USER) {
          this.setState({ facebookValidationError: error.message });
          this.setLoadingState(false);
        } else if (error.api_error_code === CONST.INVALID_PASSWORD_GOOGLE_USER) {
          this.setState({ googleValidationError: error.message });
          this.setLoadingState(false);
        } else if (error.api_error_code === CONST.LOGIN_MFA_REQUIRED) {
          // record successful email+password login
          loginUtil.storeLoginCredentialsToBrowser(creds);
          // redirect to the LoginMfa screen
          this.props.onLoginWithMfa(creds, error.data);
        } else {
          this.setState({ emailValidationError: error.message });
          this.setLoadingState(false);
        }
      },
    );
  }

  contextLogSignupLoginEvent(event: string) {
    const { contextLog, viewer } = this.props;
    if (viewer.isAuth) {
      contextLog(event, {
        login_state: viewer.isLimitedLogin
          ? PCONST.LoginStates.LIMITED
          : PCONST.LoginStates.DEFAULT,
        signup_login_method: PCONST.SignupLoginMethods.EMAIL,
      });
    } else {
      contextLog(event, {
        signup_login_method: PCONST.SignupLoginMethods.EMAIL,
      });
    }
  }

  register = () => {
    this.setLoadingState(true);
    const {
      age,
      container,
      email,
      name,
      password,
      showSignupWelcomeLoadingFn,
      signupSource,
      requestContext,
      showResponseMessageModal,
    } = this.props;
    const resourceOptions = {
      container,
      email,
      password,
      age,
      country: this.context.country,
      signupSource,
      first_name: name,
      last_name: '',
      hybridTier: this.context.hybridTier,
      page: this.context.pageType,
    };

    trackRegisterAction('one_step_age_signup_complete');

    // todo(sophia) We need to flush experiment triggering immediately (current experiment client default wraps
    // activate resource call in a queued resource call with 5 second delay). Longterm soln is to support immediate
    // flush in ExperimentChecker class.
    // https://paper.dropbox.com/doc/Web-Welcome-Loading-Screen-Experiments-Triggering-Issues--AMlvl51RMVYopimH6DjeJ8EzAg-BwroITTNabE6ckNqNRoqJ
    const inWelcomeExperiment = new SingleTreatmentExp(
      requestContext,
      'web_unauth_email_welcome',
    ).enabled();
    requestContext.experiments.flush();
    if (!showResponseMessageModal && showSignupWelcomeLoadingFn && inWelcomeExperiment) {
      showSignupWelcomeLoadingFn(true);
    }

    UserRegistration.register(resourceOptions).then(this.handleSignupSuccess, (error) => {
      this.contextLogSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_FAILURE);
      this.setLoadingState(false);
      if (error.api_error_code === CONST.API_ERROR_UNDERAGE_REGISTER_ATTEMPT) {
        Cookie.set(PCONST.COOKIE_REGISTER_RESTRICT, '1');
        this.showUnderAgeError();
      } else if (error.api_error_code === CONST.SIGNUP_INVALID_EMAIL) {
        showError(i18n._('Your email is invalid.'));
      } else {
        showError(i18n._('Sorry! Something went wrong on our end.'));
      }
    });
    this.contextLogSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_ATTEMPT);
  };

  handleSignupSuccess = () => {
    const { showResponseMessageModal } = this.props;

    this.contextLogSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_SUCCESS);
    trackRegisterAction('unauth.signup_one_step.completed');
    if (showResponseMessageModal) {
      showResponseMessageModal(true);
    } else {
      loginUtil.handleSignupRedirect(this.props.nextUrlParam, this.props.disablePostSignupNextUrl);
    }
  };

  inHideAgeInputExistingEmailExp() {
    // Trigger hide age input for existing email account experiment only if age is mandatory for user country
    const { country } = this.context;
    const hideAgeInputExistingEmailExp = new MultiTreatmentExp(
      this.props.requestContext,
      'web_unauth_hide_age_existing_email_v2',
    );
    return (
      (registrationRequiresAgeGDPR(country) || registrationRequiresAgeEN(country)) &&
      (hideAgeInputExistingEmailExp.check('enabled') ||
        hideAgeInputExistingEmailExp.check('enabled_with_copy'))
    );
  }

  shouldRequireAge = () => {
    const { country } = this.context;
    return registrationRequiresAgeGDPR(country) || registrationRequiresAgeEN(country);
  };

  validateAgeRequirement = () => {
    const { userAgent } = this.context;
    let ageError = false;
    if (!userAgent.isMobile && this.shouldRequireAge()) {
      ageError = runValidators(this.props.age || '', this.ageValidators);
      if (ageError) {
        this.setState({ ageValidationError: ageError });
        this.setLoadingState(false);
        trackRegisterAction(`unauth.login.error.CLIENT_ERROR.invalidage.${pageName}`);
      }
    }
    return ageError;
  };

  validatePasswordRequirement = () => {
    const passwordError = runValidators(this.props.password, this.passwordValidators);
    if (passwordError) {
      this.setState({ passwordValidationError: passwordError });
      this.setLoadingState(false);
      trackRegisterAction(`unauth.login.error.CLIENT_ERROR.malformpwd.${pageName}`);
    }
    return passwordError;
  };

  completeRegistration = (e: ?SyntheticEvent<>) => {
    this.setState({ emailValidationError: null });
    this.setLoadingState(false);
    trackRegisterAction('unauth.signup_step_1.completed');

    if (!this.context.userAgent.isMobile) {
      this.register();
    }
  };

  checkEmailExistsWithCompletion = (
    email: string,
    handleEmailExists: () => void,
    handleEmailNotExist: () => void,
  ) => {
    const emailExistsResource = ResourceFactory.create('EmailExistsResource', {
      email,
    });
    emailExistsResource.callGet({ emailValidationError: false }).then(
      (resourceResponse) => {
        if (resourceResponse.resource_response.data === false) {
          // Email not associated with existing account
          handleEmailNotExist();
        } else {
          // Email associated with existing accoutn
          handleEmailExists();
        }
      },
      () => {
        const error = i18n._("Hmm...that doesn't look like an email address");
        this.setState({ emailValidationError: error });
      },
    );
  };

  validateEmailAvailable(e: SyntheticEvent<>) {
    this.setLoadingState(true);
    const { hybridTier, pageType } = this.context;
    const { container, email, password, seoUnauthExperiments } = this.props;

    const handleEmailNotExist = () => {
      if (!this.validateAgeRequirement() && !this.validatePasswordRequirement()) {
        if (isMistypedGmail(email)) {
          this.setLoadingState(false);
          this.setState({
            emailFieldHasError: true,
          });
          return;
        }
        this.completeRegistration(e);
      }
    };
    const handleEmailExists = () => {
      const creds = {
        username_or_email: email,
        password,
      };
      const loggingData = {
        container,
        hybridTier,
        page: pageType,
      };
      login(creds, loggingData, seoUnauthExperiments).then(
        (response) => {
          this.handleLoginSuccess(creds);
        },
        (error) => {
          this.handleLoginFailure(error, creds);
        },
      );
      this.contextLogSignupLoginEvent(PCONST.thrift.EventType.LOGIN_ATTEMPT);
    };
    this.checkEmailExistsWithCompletion(email, handleEmailExists, handleEmailNotExist);
  }

  loginThenValidateEmailAvailble(e: SyntheticEvent<>) {
    this.setLoadingState(true);
    const { hybridTier, pageType } = this.context;
    const { email, password, seoUnauthExperiments } = this.props;
    const creds = {
      username_or_email: email,
      password,
    };
    const loggingData = {
      container: this.props.container,
      hybridTier,
      page: pageType,
    };

    // Attempt to login user first
    login(creds, loggingData, seoUnauthExperiments).then(
      (response) => {
        this.handleLoginSuccess(creds);
      },
      (error) => {
        // If login failed due to wrong email, attempt registration
        if (error.api_error_code === CONST.INVALID_EMAIL_ERROR_CODE) {
          if (!this.validateAgeRequirement() && !this.validatePasswordRequirement()) {
            this.completeRegistration(e);
          }
        } else {
          // Login failed due to bad password, etc, so show login errors
          this.handleLoginFailure(error, creds, true);
        }
      },
    );
    this.contextLogSignupLoginEvent(PCONST.thrift.EventType.LOGIN_ATTEMPT);
  }

  updateAccountRecoveryModal = (modalType: ?ModalTypes) =>
    this.setState({ accountRecoveryModalType: modalType });

  handleSignupWithEmailClick = () => {
    trackRegisterAction('unauth_signup_email_btn_click');
    this.setState({ showRegisterForm: true });
    if (this.props.onShowRegisterForm) {
      this.props.onShowRegisterForm();
    }
  };

  renderSocialButtons = () => {
    const { sm } = this.props;
    // Have google button on top when facebook button has friends
    const facebookButton = (
      <FacebookConnectButton
        buttonText={i18n._('Continue with Facebook')}
        container={this.props.container}
        disablePostSignupNextUrl={this.props.disablePostSignupNextUrl}
        onSocialConnectVerified={this.props.onSocialConnectVerified}
        onFBEmailDeniedPerm={this.props.onFBEmailDeniedPerm}
        loadingIndicatorCallback={this.props.loadingIndicatorCallback}
        onLoginWithMfa={this.props.onLoginWithMfa}
        onLoginWithSuspended={this.onLoginWithSuspended}
        showLoginWelcomeLoadingFn={this.props.showLoginWelcomeLoadingFn}
        showSignupWelcomeLoadingFn={this.props.showSignupWelcomeLoadingFn}
        tooltip={this.state.facebookValidationError}
        useGestaltTooltip
        showResponseMessageModal={this.props.showResponseMessageModal}
      />
    );
    const googleButton = (
      <div
        ref={(c) => {
          this.googleButton = c;
        }}
      >
        {sm !== '1' && (
          <GoogleConnectButton
            action="signup"
            container={this.props.container}
            disablePostSignupNextUrl={this.props.disablePostSignupNextUrl}
            nextUrlParam={'/'}
            onSocialConnectVerified={this.props.onSocialConnectVerified}
            onLoginWithMfa={this.props.onLoginWithMfa}
            onLoginWithSuspended={this.onLoginWithSuspended}
            onLoginWithVoluntarilyDeactivated={this.onLoginWithVoluntarilyDeactivated}
            showLoginWelcomeLoadingFn={this.props.showLoginWelcomeLoadingFn}
            showSignupWelcomeLoadingFn={this.props.showSignupWelcomeLoadingFn}
            tooltip={this.state.googleValidationError}
            showResponseMessageModal={this.props.showResponseMessageModal}
          />
        )}
      </div>
    );
    return (
      <div style={styles.socialButtonContainer}>
        {facebookButton}
        <div style={styles.socialButtonSeparator} />
        {googleButton}
      </div>
    );
  };

  renderSignupForm = () => {
    const {
      age,
      email,
      setWelcomeBackMessageForExistingEmail,
      inputStyleOverrides,
      newEmail,
      onAgeChange,
      password,
      suppressFocus,
      updateEmail,
    } = this.props;

    return this.state.showRegisterForm ? (
      <RegisterForm
        age={age}
        ageValidationError={this.state.ageValidationError}
        buttonText={this.getButtonText()}
        buttonTextLabel={i18n._(
          'Continue creating your Pinterest account',
          'Accessiblity button text to continue creating an account',
        )}
        checkEmailExistsWithCompletion={this.checkEmailExistsWithCompletion}
        email={email}
        emailFieldHasError={this.state.emailFieldHasError}
        emailText={i18n._('Email')}
        emailLabel={i18n._('Enter your email', 'Accessiblity input text on login screen')}
        emailValidationError={this.state.emailValidationError}
        setWelcomeBackMessageForExistingEmail={setWelcomeBackMessageForExistingEmail}
        onTypoSuggestionClick={this.handleTypoSuggestionClick}
        inHideAgeInputExistingEmailExp={this.inHideAgeInputExistingEmailExp()}
        inputStyleOverrides={inputStyleOverrides}
        newEmail={newEmail}
        onAgeChange={onAgeChange}
        onButtonClick={this.handleButtonClick.bind(this)}
        onEmailChange={this.handleEmailChange}
        onPasswordChange={this.handlePasswordChange}
        password={password}
        passwordText={i18n._('Create a password')}
        passwordValidationError={this.state.passwordValidationError}
        showAgeInput={this.shouldRequireAge()}
        suppressFocus={suppressFocus}
        updateEmail={updateEmail}
        isSignupForm
      />
    ) : (
      <div data-test-id="emailSignUpButton">
        <Button
          text={i18n._(
            'Sign up with email',
            'the description of sign up with email button on sign up modal',
          )}
          color="red"
          onClick={this.handleSignupWithEmailClick}
        />
      </div>
    );
  };

  renderReorderedButtons = () => {
    const additionalStyle = { marginBottom: '10px' };
    const { signupSeparatorContinuous } = this.props;
    return (
      <div>
        {this.renderSocialButtons()}
        {this.state.showRegisterForm ? (
          <div style={additionalStyle}>
            <AlternativesSeparator signupSeparatorContinuous={signupSeparatorContinuous} />
          </div>
        ) : null}
        {this.renderSignupForm()}
      </div>
    );
  };

  renderSocialButtonsOnly = () => {
    this.renderSocialButtons();
  };

  renderOriginalForm = () => {
    const { signupSeparatorContinuous } = this.props;
    return (
      <div>
        {this.renderSignupForm()}
        {this.state.showRegisterForm ? (
          <AlternativesSeparator signupSeparatorContinuous={signupSeparatorContinuous} />
        ) : null}
        {this.renderSocialButtons()}
      </div>
    );
  };

  renderButtons = () => {
    const { sm } = this.props;
    if (sm === '2') {
      return this.renderReorderedButtons();
    } else if (sm === '1') {
      return this.renderSocialButtonsOnly();
    } else {
      return this.renderOriginalForm();
    }
  };

  showUnderAgeError = () => {
    this.setState({
      ageValidationError: i18n._(
        "Sorry, you can't sign up yet. You’re not old enough to use Pinterest yet.",
        'Error message when under age user trying to register',
      ),
    });
  };

  render() {
    const { additionalChildren, dark } = this.props;
    const signupContainerStyles = styles.signupContainer;

    const acctRecovery = (
      <AccountRecovery
        modalType={this.state.accountRecoveryModalType}
        updateAccountRecoveryModal={this.updateAccountRecoveryModal}
        email={this.props.email}
        onLoginWithMfa={this.props.onLoginWithMfa}
        onLoginWithSuspended={this.onLoginWithSuspended}
        onLoginWithVoluntarilyDeactivated={this.onLoginWithVoluntarilyDeactivated}
      />
    );

    return (
      <div style={signupContainerStyles}>
        {acctRecovery}
        {this.renderButtons()}
        <TermsOfService dark={dark} />
        {additionalChildren}
      </div>
    );
  }
}

const mapStateToProps = (state: State): ReduxProps => ({
  viewer: state.viewer,
});

EmailPasswordStep.contextTypes = {
  country: PropTypes.string,
  locale: PropTypes.string.isRequired,
  userAgent: PropTypes.shape({
    isMobile: PropTypes.bool.isRequired,
  }).isRequired,
  hybridTier: PropTypes.string,
  pageType: PropTypes.string,
};

export default compose(
  withRequestContext,
  connect(mapStateToProps),
  withRouter,
  withUnauthSeoExperiments,
  withContextLog,
)(EmailPasswordStep);
